import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { useWealthboxClientLink } from '../../../../../api/wealthbox'
import { postNamedQuery } from '../../../../../service'
import { QUERY_KEYS } from '../../../../../api/queryKeys'
import { scopeRequiresClientLink, buildTaskParams } from '../shared/wealthboxQueryHelpers'
import presets from './presets'

// Data hook for the `wealthbox-list` component type. Mirrors
// useWealthboxMetricPreset but returns an array of items rather than a count.
// Reuses the same scope/client-link plumbing so a wealthbox-metric and a
// wealthbox-list configured against the same preset+scope share intuition.
//
// The per-preset `buildRequest(config)` returns `{ params, scopeFields }` —
// those compose with the scope-bound client filter via `buildTaskParams` so
// the listTasks endpoint receives the same shape across both components.
export default function useWealthboxListPreset (presetKey, config = {}) {
  const { clientId, userId } = useAppContext()
  const scope = config.scope || 'client'
  const needsLink = scopeRequiresClientLink(scope)
  const link = useWealthboxClientLink(clientId, { enabled: needsLink })

  const preset = presets[presetKey]
  const contactId = link.data?.externalId ?? null
  const limit = config?.limit ?? preset?.defaults?.limit ?? 5

  const { params: presetParams, scopeFields } = useMemo(
    () => (preset ? preset.buildRequest(config) : { params: {}, scopeFields: {} }),
    [preset, config]
  )

  // The preset declares which Wealthbox entity it operates against
  // ('tasks' | 'notes' | 'events'). We route to the matching server query
  // (`listTasks` / `listNotes` / `listEvents`) and unwrap the response from
  // its entity-specific key so the hook always returns an `items` array.
  //
  // Notes are a wart: Wealthbox returns the list under `status_updates`,
  // not `notes` (verified against the live dev.wealthbox.com docs Aug
  // 2026 - "Notes are returned in the `status_updates` array"). Don't
  // assume entity name matches response key.
  const domain = preset?.defaults?.domain || 'tasks'
  const QUERY_BY_DOMAIN = {
    tasks: { query: 'listTasks', responseKey: 'tasks' },
    notes: { query: 'listNotes', responseKey: 'status_updates' },
    events: { query: 'listEvents', responseKey: 'events' }
  }
  const route = QUERY_BY_DOMAIN[domain] || QUERY_BY_DOMAIN.tasks

  // Wealthbox doesn't support server-side sort on most list endpoints, so we
  // ask for `limit * 2` so we have headroom to sort client-side without
  // immediately hitting the wrong items. Once Wealthbox adds `order` for
  // tasks/notes, this can drop to just `limit`.
  const perPage = Math.max(limit * 2, 10)

  const itemsQuery = useQuery({
    queryKey: [
      QUERY_KEYS.wealthboxDomainQuery, 'list-preset',
      userId, clientId, presetKey, scope,
      config.createdByScope, config.updatedWithinDays, config.staleAfterDays,
      config.lookaheadDays, config.order, config.includeCompleted, config.taskType,
      config.teamId, config.previewLength, limit, contactId
    ],
    queryFn: async () => {
      const baseParams = buildTaskParams({
        scope,
        contactId,
        extra: { per_page: perPage, ...presetParams }
      })
      if (!baseParams && scope === 'client') {
        return { items: [], totalCount: 0 }
      }
      const { data } = await postNamedQuery('coreData', 'execute-wealthbox-domain-query', {
        query: route.query,
        clientId,
        request: {
          params: baseParams,
          scope,
          ...(scopeFields || {})
        }
      })
      const items = data?.[route.responseKey] ?? []
      return {
        items,
        totalCount: data?.meta?.total_count ?? items.length
      }
    },
    enabled: needsLink
      ? !!preset && link.data?.linked === true && !!contactId
      : !!preset
  })

  const merged = useMemo(() => {
    if (!preset) return { title: 'Unknown Preset', icon: null }
    return {
      title: config.hideTitle ? null : (config.title ?? preset.defaults.title),
      icon: config.hideIcon ? null : (config.icon ?? preset.defaults.icon)
    }
  }, [preset, config.title, config.hideTitle, config.icon, config.hideIcon])

  return {
    items: itemsQuery.data?.items ?? [],
    totalCount: itemsQuery.data?.totalCount ?? 0,
    limit,
    contactId,
    Content: preset?.Content,
    isLoading: needsLink
      ? link.isLoading || itemsQuery.isFetching
      : itemsQuery.isFetching,
    notLinked: needsLink ? link.data?.linked === false : false,
    error: needsLink
      ? link.error || itemsQuery.error
      : itemsQuery.error,
    ...merged
  }
}
